import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'

export function SteelFloorPlate() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [q, setQ] = useState('')
  const [e, setE] = useState(200000)
  const [fy, setFy] = useState(250)
  const [gammaSteel, setGammaSteel] = useState(78.5)
  const [gammaQ, setGammaQ] = useState(1.5)
  const [gammaG, setGammaG] = useState(1.25)

  const [result, setResult] = useState(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'steel-floor-plate-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const saved = localStorage.getItem(HISTORY_KEY)
    if (saved) {
      try {
        setHistory(JSON.parse(saved))
      } catch {
        // ignore
      }
    }
  }, [])

  const saveToHistory = useCallback((entry) => {
    setHistory((prev) => {
      const updated = [{ ...entry, timestamp: new Date().toISOString(), id: Date.now() }, ...prev].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      return updated
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return
      const entry = history[index]
      setA(entry.a)
      setB(entry.b)
      setQ(entry.q)
      setE(entry.e)
      setFy(entry.fy)
      setGammaSteel(entry.gammaSteel)
      setGammaQ(entry.gammaQ)
      setGammaG(entry.gammaG)
      setResult(entry.result)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) loadFromHistory(newIndex)
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) return 'Campo obrigatório'
    const num = Number(value)
    if (isNaN(num)) return 'Valor deve ser um número'
    if (num <= 0) return 'Valor deve ser maior que 0'
    return null
  }, [])

  useEffect(() => {
    const newErrors = {
      a: validateField('a', a),
      b: validateField('b', b),
      q: validateField('q', q),
      e: validateField('e', e),
      fy: validateField('fy', fy),
      gammaSteel: validateField('gammaSteel', gammaSteel),
      gammaQ: validateField('gammaQ', gammaQ),
      gammaG: validateField('gammaG', gammaG),
    }
    setErrors(newErrors)
    setIsFormValid(!Object.values(newErrors).some(Boolean))
  }, [a, b, q, e, fy, gammaSteel, gammaQ, gammaG, validateField])

  const handleBlur = (fieldName) => setTouched((prev) => ({ ...prev, [fieldName]: true }))

  async function handleCalculate(event) {
    event.preventDefault()
    const hasExistingResults = result !== null
    setIsLoading(true)
    if (hasExistingResults) setIsRecalculating(true)

    try {
      const payload = {
        a: Number(a),
        b: Number(b),
        q: Number(q),
        e: Number(e),
        fy: Number(fy),
        gammaSteel: Number(gammaSteel),
        gammaQ: Number(gammaQ),
        gammaG: Number(gammaG),
      }

      const response = await api.post('/steel/floor-plate', payload)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        a,
        b,
        q,
        e,
        fy,
        gammaSteel,
        gammaQ,
        gammaG,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)
      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }
      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) setTimeout(() => setIsRecalculating(false), 500)
    }
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  function handleSave() {
    const stateData = { a, b, q, e, fy, gammaSteel, gammaQ, gammaG, result }
    const blob = new Blob([JSON.stringify(stateData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Aço Chapa de Piso.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result)
        setA(d.a)
        setB(d.b)
        setQ(d.q)
        setE(d.e)
        setFy(d.fy)
        setGammaSteel(d.gammaSteel ?? 78.5)
        setGammaQ(d.gammaQ ?? 1.5)
        setGammaG(d.gammaG ?? 1.25)
        setResult(d.result ?? null)
        toast.success('Estado carregado com sucesso!')
      } catch {
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Chapa de piso</span>
          </div>
          <h1>🟫 Chapa de piso em aço</h1>
          <p className="subtitle">
            Dimensionamento de chapas de piso em aço com verificação por deformação e resistência
          </p>
          <div className="badges">
            <Badge>📘 ABNT NBR 8800:2008</Badge>
            <Badge>📏 Chapas de piso</Badge>
            <Badge>⚖️ Verificação e peso próprio</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Dimensiona a espessura de chapas de piso em aço com base nos coeficientes α e β da tabela de relação de
              aspecto. Realiza a estimativa inicial e verifica com o peso próprio da chapa adotada.
            </p>
            <p>
              A API normaliza internamente as dimensões (a ≥ b). Os valores exibidos correspondem às dimensões
              normalizadas usadas no cálculo.
            </p>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Dimensão a (cm)</span>
              <ValidatedInput
                name="a"
                type="number"
                value={a}
                onChange={(e) => setA(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Dimensão b (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b}
                onChange={(e) => setB(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Carregamento</h3>
            <InputDiv>
              <span>Carga distribuída q (kN/m²)</span>
              <ValidatedInput
                name="q"
                type="number"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>γ da sobrecarga (γQ)</span>
              <ValidatedInput
                name="gammaQ"
                type="number"
                value={gammaQ}
                onChange={(e) => setGammaQ(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>γ do peso próprio (γG)</span>
              <ValidatedInput
                name="gammaG"
                type="number"
                value={gammaG}
                onChange={(e) => setGammaG(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Material</h3>
            <InputDiv>
              <span>Módulo de elasticidade E (MPa)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e}
                onChange={(ev) => setE(ev.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência ao escoamento fy (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy}
                onChange={(ev) => setFy(ev.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Peso específico do aço γ (kN/m³)</span>
              <ValidatedInput
                name="gammaSteel"
                type="number"
                value={gammaSteel}
                onChange={(e) => setGammaSteel(e.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('floor-plate-file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input
              type="file"
              id="floor-plate-file-input"
              accept=".json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </form>
        </Card>

        {result !== null && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>

              <MathDiv>
                <h3>Dimensões normalizadas</h3>
                <BlockMath
                  math={`a = ${result.dimensions.a_cm} \\, \\text{cm} \\quad b = ${result.dimensions.b_cm} \\, \\text{cm}`}
                />

                <h3>Coeficientes da tabela</h3>
                <BlockMath math={`\\frac{a}{b} = ${result.floorCoefficients.aOverB.toFixed(3)}`} />
                <BlockMath
                  math={`\\alpha = ${result.floorCoefficients.alpha} \\quad \\beta = ${result.floorCoefficients.beta}`}
                />

                <h3>Pré-dimensionamento</h3>
                <BlockMath math={`t_1 = ${result.t1_mm.toFixed(2)} \\, \\text{mm} \\quad \\text{(deformação)}`} />
                <BlockMath math={`t_2 = ${result.t2_mm.toFixed(2)} \\, \\text{mm} \\quad \\text{(resistência)}`} />
                <BlockMath math={`t_{req} = ${result.requiredThickness_mm.toFixed(2)} \\, \\text{mm}`} />

                <h3>Espessura comercial adotada</h3>
                <BlockMath math={`\\boxed{t = ${result.plateThickness_mm.toFixed(2)} \\, \\text{mm}}`} />

                <h3>Verificação com peso próprio</h3>
                <BlockMath math={`g_{pp} = ${result.check.selfWeight_knm2.toFixed(4)} \\, \\text{kN/m}^2`} />
                <BlockMath
                  math={`t_1 = ${result.check.t1_mm.toFixed(2)} \\, \\text{mm} \\quad t_2 = ${result.check.t2_mm.toFixed(2)} \\, \\text{mm}`}
                />
                <BlockMath math={`t_{req,v} = ${result.check.requiredThickness_mm.toFixed(2)} \\, \\text{mm}`} />

                {result.check.passes ? (
                  <p style={{ color: 'green', fontWeight: 'bold' }}>
                    ✅ Chapa adotada verificada — {result.plateThickness_mm.toFixed(2)} mm ≥{' '}
                    {result.check.requiredThickness_mm.toFixed(2)} mm
                  </p>
                ) : (
                  <p style={{ color: 'red', fontWeight: 'bold' }}>❌ Chapa não verificada — espessura insuficiente</p>
                )}
              </MathDiv>
            </Result>

            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>

              <MathDiv>
                <h3>Dados de entrada (normalizados)</h3>
                <BlockMath
                  math={`a = ${result.dimensions.a_cm} \\, \\text{cm}, \\quad b = ${result.dimensions.b_cm} \\, \\text{cm}`}
                />
                <BlockMath math={`q = ${result.loads.q_knm2} \\, \\text{kN/m}^2`} />
                <BlockMath
                  math={`E = ${Number(e).toFixed(0)} \\, \\text{MPa}, \\quad f_y = ${Number(fy).toFixed(0)} \\, \\text{MPa}`}
                />
                <BlockMath math={`\\gamma_{\\text{aço}} = ${result.loads.gammaSteel_knm3} \\, \\text{kN/m}^3`} />
                <BlockMath math={`\\gamma_Q = ${result.loads.gammaQ}, \\quad \\gamma_G = ${result.loads.gammaG}`} />

                <h3>Relação de aspecto e coeficientes</h3>
                <BlockMath
                  math={`\\frac{a}{b} = \\frac{${result.dimensions.a_cm}}{${result.dimensions.b_cm}} = ${result.floorCoefficients.aOverB.toFixed(3)}`}
                />
                <BlockMath
                  math={`\\alpha = ${result.floorCoefficients.alpha}, \\quad \\beta = ${result.floorCoefficients.beta}`}
                />

                <h3>Pré-dimensionamento (sem peso próprio)</h3>
                <BlockMath math={String.raw`t_1 = \sqrt[3]{\frac{250 \cdot \alpha \cdot q \cdot b^4}{E \cdot a}}`} />
                <BlockMath math={`t_1 = ${result.t1_mm.toFixed(2)} \\, \\text{mm}`} />
                <BlockMath math={String.raw`t_2 = b \cdot \sqrt{\frac{\beta \cdot q}{f_y}}`} />
                <BlockMath math={`t_2 = ${result.t2_mm.toFixed(2)} \\, \\text{mm}`} />
                <BlockMath
                  math={`t_{req} = \\max(t_1, t_2) = ${result.requiredThickness_mm.toFixed(2)} \\, \\text{mm}`}
                />
                <BlockMath
                  math={`\\Rightarrow t_{comercial,0} = ${(result.iterations?.[0]?.plateThickness_mm ?? result.plateThickness_mm).toFixed(2)} \\, \\text{mm}`}
                />

                <h3>Verificação com peso próprio</h3>
                <BlockMath
                  math={`g_{pp} = \\gamma_{\\text{aço}} \\cdot \\frac{t}{1000} = ${result.check.selfWeight_knm2.toFixed(4)} \\, \\text{kN/m}^2`}
                />
                <BlockMath
                  math={`t_{1,v} = ${result.check.t1_mm.toFixed(2)} \\, \\text{mm} \\quad \\text{(deformação com PP)}`}
                />
                <BlockMath
                  math={`t_{2,v} = ${result.check.t2_mm.toFixed(2)} \\, \\text{mm} \\quad \\text{(resistência com PP)}`}
                />
                <BlockMath math={`t_{req,v} = ${result.check.requiredThickness_mm.toFixed(2)} \\, \\text{mm}`} />
                <BlockMath
                  math={
                    result.check.passes
                      ? `${result.plateThickness_mm.toFixed(2)} \\geq ${result.check.requiredThickness_mm.toFixed(2)} \\, \\text{mm} \\quad \\checkmark`
                      : `${result.plateThickness_mm.toFixed(2)} < ${result.check.requiredThickness_mm.toFixed(2)} \\, \\text{mm} \\quad \\times`
                  }
                />
                <BlockMath math={`\\boxed{t_{adotado} = ${result.plateThickness_mm.toFixed(2)} \\, \\text{mm}}`} />

                {result.iterations && result.iterations.length > 0 && (
                  <>
                    <h3>Iterações sobre espessuras comerciais</h3>
                    {result.iterations.map((iter, i) => (
                      <div key={i}>
                        <h4>
                          Espessura {iter.plateThickness_mm?.toFixed(2)} mm {iter.passes ? '✅' : '❌'}
                        </h4>
                        <BlockMath math={`g_{pp} = ${iter.selfWeight_knm2?.toFixed(4)} \\, \\text{kN/m}^2`} />
                        <BlockMath
                          math={`t_{1,v} = ${iter.t1_mm?.toFixed(2)} \\, \\text{mm} \\quad t_{2,v} = ${iter.t2_mm?.toFixed(2)} \\, \\text{mm}`}
                        />
                        <BlockMath math={`t_{req,v} = ${iter.requiredThickness_mm?.toFixed(2)} \\, \\text{mm}`} />
                        <BlockMath
                          math={
                            iter.passes
                              ? `${iter.plateThickness_mm?.toFixed(2)} \\geq ${iter.requiredThickness_mm?.toFixed(2)} \\, \\text{mm} \\quad \\checkmark`
                              : `${iter.plateThickness_mm?.toFixed(2)} < ${iter.requiredThickness_mm?.toFixed(2)} \\, \\text{mm} \\quad \\times`
                          }
                        />
                      </div>
                    ))}
                  </>
                )}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
